import { Suspense, use } from 'react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Link, useLoaderData, useNavigate } from 'react-router'
import { BranchPageHeading } from '@/components/subscription/branch-page-heading'
import { Panel } from '~/components/subscription/panel'
import { ZendeskWidget } from '@/components/zendesk-widget'
import { formatDate, formatTime } from '@/lib/shipment'
import { findShippingStatus } from '@/lib/order'
import { createClient } from '@/api-client'
import { Me } from '@/api-client/Me'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { Cards } from '@/api-client/Cards'
import { SubscriptionInventory } from '@/api-client/data-contracts'
import howto from './images/howto.jpg'

export const loader = async () => {
  const account = await fetchMe()
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  let placements = { data: { data: [] } } as Awaited<ReturnType<typeof fetchPlacements>>
  let address = { data: { data: { attributes: {} } } } as Awaited<ReturnType<typeof fetchAddress>>
  let schedules = { data: { data: [] } } as Awaited<ReturnType<typeof fetchSchedules>>
  let product = { data: { data: null } } as Awaited<ReturnType<typeof fetchProduct>>
  let orders = { data: { data: [] } } as Awaited<ReturnType<typeof fetchOrders>>
  if (subscription) {
    ;[placements, address, product, orders] = await Promise.all([
      fetchPlacements(subscription.id),
      fetchAddress(subscription.id),
      fetchProduct(subscription.id),
      fetchOrders(subscription.id),
    ])
    schedules = await fetchSchedules(address.data.data.attributes.postal_code)
  }

  return [account, subscriptions, placements, address, schedules, product, orders] as const
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'green' })
}

function fetchPlacements(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(id)
}

function fetchAddress(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, category: 'green', upcoming: '1', limit: '9' })
}

function fetchProduct(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdProduct(id)
}

function fetchOrders(id: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdOrders(id, { include: ['order_items.shipping_statuses'] })
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

const creditCardPromise = fetchDefaultCard()
  .then((res) => res.data.data)
  .catch(() => null)

export const SubscriptionBranchMypageTop: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: subscriptions },
    },
    {
      data: { data: placements },
    },
    {
      data: { data: address },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: product },
    },
    {
      data: { data: orders, included: ordersIncluded },
    },
  ] = useLoaderData<typeof loader>()
  const creditCard = use(creditCardPromise)
  const navigate = useNavigate()

  const subscription = subscriptions[0]
  const nextPlacement = placements[0]
  const orderableName = product?.attributes.name
  const date = subscription && new Date(subscription.attributes.next_deliver_at * 1000)
  const weekday = date ? format(date, 'E', { locale: ja }) : ''
  const currentSchedules =
    subscription?.relationships.inventories.data
      .map((inventory: SubscriptionInventory) => schedules.find((s) => s.relationships.inventory.data.id === inventory.id))
      .sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0]) || []
  const latestShipping = findShippingStatus(orders[0], ordersIncluded)
  const latestShippingStatus = latestShipping?.attributes?.status

  const handleClickSkip: React.MouseEventHandler<HTMLButtonElement> = async () => {
    if (!window.confirm('次回のお届けをスキップしますか？')) return

    try {
      await createClient(Subscriptions).postSubscriptionsSubscriptionIdPlacementsIdSkip(subscription.id, nextPlacement.id)
      await navigate('/subscription/branch/mypage', { state: { message: '次回のお届けをスキップしました' } })
    } catch (error) {
      console.error(error)
    }
  }

  const handleClickUnskip: React.MouseEventHandler<HTMLButtonElement> = async () => {
    if (!window.confirm('次回のお届けのスキップを取り消しますか？')) return

    try {
      await createClient(Subscriptions).postSubscriptionsSubscriptionIdPlacementsIdUnskip(subscription.id, nextPlacement.id)
      await navigate('/subscription/branch/mypage', { state: { message: '次回のお届けのスキップを取り消しました' } })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-6 items-start w-full">
        <div className="flex flex-col gap-1">
          <BranchPageHeading
            main="マイページ"
            sub={subscription && `${formatTime(1000 * subscription.attributes.skip_deadline)}まで今後のお届け分のスキップ・配送先などの変更が可能です`}
          />
        </div>
        {subscription ? (
          <>
            {['not_shipped', 'preparation', 'transit'].includes(latestShippingStatus) && (
              <Panel>
                <h3 className="text-[15px] font-bold text-black leading-relaxed">次回の到着日</h3>
                <div className="flex items-center gap-1">
                  <span className="text-[19px] font-bold text-black leading-relaxed">
                    {formatDate(1000 * latestShipping.attributes.deliver_at, 'M/d（E）')}
                  </span>
                  <span className="inline-block bg-[#eeeeee] rounded-sm px-1.5 py-0.5 text-[12px] font-bold">{latestShipping.attributes.status_label}</span>
                </div>
                {latestShipping?.attributes?.tracking_url && (
                  <a href={latestShipping.attributes.tracking_url} className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                    追跡する
                  </a>
                )}
              </Panel>
            )}

            <Panel>
              <h3 className="text-[15px] font-bold text-black leading-relaxed">今後のお届け予定</h3>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[19px] font-bold text-black leading-relaxed ${nextPlacement?.attributes?.status === 'skipped' ? 'line-through' : ''}`}
                    >
                      {currentSchedules[0] && formatDate(currentSchedules[0].attributes.deliver_at[0] * 1000, 'M/d（E）')}
                    </span>
                    {nextPlacement?.attributes?.status === 'skipped' && (
                      <button
                        type="button"
                        onClick={handleClickUnskip}
                        className="text-[13px] font-bold text-black underline decoration-solid leading-relaxed tracking-wide"
                      >
                        スキップを取り消す
                      </button>
                    )}
                    {nextPlacement?.attributes?.status === 'pending' && (
                      <button
                        type="button"
                        onClick={handleClickSkip}
                        className="text-[13px] font-bold text-black underline decoration-solid leading-relaxed tracking-wide"
                      >
                        お届けをスキップ
                      </button>
                    )}
                  </div>

                  {currentSchedules.length > 1 && (
                    <div className="flex flex-col gap-1">
                      {currentSchedules.slice(1).map((schedule) => (
                        <p key={schedule.id} className="text-[15px] font-bold text-black leading-relaxed">
                          {formatDate(schedule?.attributes.deliver_at[0] * 1000, 'M/d（E）')}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-0.5 text-[#828282]">
                  <div className="flex gap-1 items-start">
                    <span className="mt-0.5">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="1" y="2.5" width="12" height="10.5" rx="1.5" />
                        <line x1="1" y1="5.5" x2="13" y2="5.5" />
                        <line x1="4.5" y1="1" x2="4.5" y2="4" />
                        <line x1="9.5" y1="1" x2="9.5" y2="4" />
                      </svg>
                    </span>
                    <p className="text-[13px] leading-relaxed tracking-wide">
                      月に{currentSchedules.length}回・{weekday}曜日にお届け
                    </p>
                  </div>
                  <div className="flex gap-1 items-start">
                    <span className="mt-0.5">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1.5 4.5l5.5 3 5.5-3" />
                        <path d="M7 13V7.5" />
                        <path d="M12.5 4.5l-5.5-3-5.5 3v5l5.5 3 5.5-3v-5z" />
                      </svg>
                    </span>
                    <p className="text-[13px] leading-relaxed tracking-wide">クロネコヤマトで配送（対面でのお受け取り）</p>
                  </div>
                </div>

                <Link
                  to="/subscription/branch/mypage/schedule"
                  className="text-[13px] font-bold text-black underline decoration-solid leading-relaxed tracking-wide"
                >
                  受け取りスケジュールを変更
                </Link>
              </div>
            </Panel>
          </>
        ) : (
          <Panel>
            <p className="text-[14px] text-black leading-relaxed tracking-wide">ご契約中の定期便はありません</p>
            <div>
              <Link to="/subscription/branch/new" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                定期便を申し込む
              </Link>
            </div>
          </Panel>
        )}

        {subscription && (
          <Panel>
            <h3 className="text-[15px] font-bold text-black leading-relaxed">お届け先</h3>
            <div className="flex flex-col items-start gap-2">
              <div className="text-[13px] text-black leading-relaxed tracking-wide">
                {address?.attributes ? (
                  <>
                    <p>〒{address.attributes.postal_code}</p>
                    <p>
                      {address.attributes.state}
                      {address.attributes.city_and_street}
                    </p>
                    <p>{address.attributes.others}</p>
                    <p>
                      {address.attributes.name}
                      {!!address.attributes.sender_name ? `（${address.attributes.sender_name}より）` : ''}
                    </p>
                  </>
                ) : (
                  <p>登録されていません</p>
                )}
              </div>
              <Link to="/subscription/branch/mypage/address" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                お届け先を変更する
              </Link>
            </div>
          </Panel>
        )}

        <Panel>
          <p className="text-[15px] font-bold text-black leading-relaxed">メールアドレス</p>
          <div className="flex flex-col gap-2">
            <p className="text-[13px] text-black leading-relaxed tracking-wide">{account.attributes.email}</p>

            <div className="flex flex-col gap-2 items-start">
              <Link to="/subscription/branch/mypage/email" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                メールアドレスを変更する
              </Link>
              <Link to="/subscription/branch/mypage/password" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                パスワードを変更する
              </Link>
            </div>
          </div>
        </Panel>

        <Panel>
          <p className="text-[15px] font-bold text-black leading-relaxed">クレジットカード</p>
          <div className="flex flex-col gap-2">
            <Suspense fallback={<></>}>
              {creditCard ? (
                <p className="text-[13px] text-black leading-relaxed tracking-wide">**** **** **** {creditCard.attributes?.last4}</p>
              ) : (
                <p className="text-[13px] text-black leading-relaxed tracking-wide">登録されていません</p>
              )}
            </Suspense>

            <div>
              <Link to="/subscription/branch/mypage/card" className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full">
                クレジットカードを変更する
              </Link>
            </div>
          </div>
        </Panel>
      </div>
      <Link to="/subscription/branch/howto" className="flex gap-4 items-center bg-white rounded-2xl p-4 shadow-[0px_8px_16px_0px_rgba(197,197,197,0.05)]">
        <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0">
          <img src={howto} alt="枝ものを飾る" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <p className="text-[11px] text-[#828282] tracking-widest">はじめてさん向け</p>
          <p className="text-[14px] font-bold text-black leading-relaxed">枝ものが届いたら？まずやってほしい3つのこと</p>
        </div>
        <svg
          className="shrink-0 text-[#828282]"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      </Link>
      <div className="flex flex-col gap-6 items-center">
        <Link to="/subscription/branch/mypage/cancel" className="text-[15px] text-black underline decoration-solid leading-relaxed">
          定期便の解約
        </Link>
        <Link to="/subscription/branch/mypage/delete" className="text-[15px] text-black underline decoration-solid leading-relaxed">
          アカウントを削除
        </Link>
      </div>

      <ZendeskWidget orderableName={orderableName} userId={account?.id} userEmail={account?.attributes.email} />
    </>
  )
}
